//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

// Composition root for the service layer: the single place the shared ApiClient
// core and the domain/auth service singletons are constructed.  Every service is
// bound to the one `apiClient` instance, so they all read the same session/auth
// state and share one transport.  Consumers import these singletons (never
// construct their own) from `@/services`.

import { ApiClient } from './apiClient';
import { AuthService } from './authService';
import { LLMService } from './llmService';
import { AsyncService } from './asyncService';
import { JobsService } from './jobsService';
import { UploadService } from './uploadService';
import { BookmarkService } from './bookmarkService';
import { ConfigService } from './configService';
import { PluginService } from './pluginService';
import { AiModelAdminService } from './aiModelAdminService';
import { PermissionProfileService } from './permissionProfileService';
import { DatasetService } from './datasetService';
import { QueryService } from './queryService';
import { AgentService } from './agentService';
import { sessionActivity } from './sessionActivity';

const apiClient = new ApiClient();

export const authService = new AuthService(apiClient);
export const llmService = new LLMService(apiClient);
export const asyncService = new AsyncService(apiClient);
export const jobsService = new JobsService(apiClient);
export const uploadService = new UploadService(apiClient);
export const bookmarkService = new BookmarkService(apiClient);
export const configService = new ConfigService(apiClient);
export const pluginService = new PluginService(apiClient);
export const aiModelAdminService = new AiModelAdminService(apiClient);
export const permissionProfileService =
  new PermissionProfileService(apiClient);
export const datasetService = new DatasetService(apiClient);
export const queryService = new QueryService(apiClient);
export const agentService = new AgentService(apiClient);

// makeRequest invokes this hook on a session-expired response; route it to the
// auth service's logout flow.  This is the one piece of cross-service wiring,
// and it lives here in the root rather than inside any single service.
apiClient.onSessionExpired = () => authService.logout();

// Idle-timeout wiring: every successful user-initiated request advances the
// controller's expiry clock, and the controller's keepalive maps to the auth
// service's /api/keepalive ping.
apiClient.onServerActivity = () => sessionActivity.recordServerActivity();
sessionActivity.keepalive = () => authService.keepalive();

export { sessionActivity } from './sessionActivity';
export { type OidcLoginResponse } from './authService';
export {
  ContractVersionError, ConversationGoneError, ProfileNotRecordedError
} from './agentService';
export { ProfileConflictError } from './permissionProfileService';
